import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import './BulkUpload.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkUpload = ({ fetchPosts }) => {
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const downloadTemplate = () => {
    const template = [
      {
        date: '2025-10-30',
        author: '@usuario_ejemplo',
        network: 'X',
        mentioned: 'Ministerio de Educación',
        persons: 'Juan Pérez',
        category: 'ATENDER',
        desc: 'Descripción de la publicación sobre posible acto irregular',
        link: 'https://x.com/usuario_ejemplo/status/123456'
      },
      {
        date: '2025-10-29',
        author: '@otro_usuario',
        network: 'Facebook',
        mentioned: 'Ministerio de Salud Pública y Asistencia Social',
        persons: 'María García',
        category: 'SEGUIMIENTO',
        desc: 'Otra publicación de ejemplo para monitoreo',
        link: 'https://facebook.com/post/789'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_carga_masiva.xlsx');
    toast.success('Plantilla descargada correctamente');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error('El archivo está vacío');
        setUploading(false);
        return;
      }

      // Validar y procesar cada fila
      const results = {
        total: jsonData.length,
        success: 0,
        errors: []
      };

      const token = localStorage.getItem('token');
      
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        
        // Skip empty rows
        if (!row || Object.keys(row).length === 0) {
          continue;
        }
        
        try {
          // Validar campos obligatorios con mensajes específicos
          const missingFields = [];
          if (!row.date) missingFields.push('fecha');
          if (!row.author) missingFields.push('autor');
          if (!row.desc) missingFields.push('descripción');
          
          if (missingFields.length > 0) {
            throw new Error(`Faltan campos: ${missingFields.join(', ')}`);
          }

          // Validar y convertir fecha
          let dateISO;
          try {
            const dateStr = String(row.date).trim();
            // Handle Excel serial dates
            if (!isNaN(dateStr) && dateStr.length < 10) {
              const excelDate = new Date((parseFloat(dateStr) - 25569) * 86400 * 1000);
              dateISO = excelDate.toISOString();
            } else {
              dateISO = new Date(dateStr).toISOString();
            }
            
            if (!dateISO || dateISO === 'Invalid Date') {
              throw new Error('Fecha inválida');
            }
          } catch (e) {
            throw new Error(`Error en fecha: ${e.message}`);
          }

          // Preparar datos con valores por defecto
          const postData = {
            date: dateISO,
            author: String(row.author).trim(),
            network: row.network ? String(row.network).trim() : 'X',
            mentioned: row.mentioned ? String(row.mentioned).trim() : 'Por definir',
            persons: row.persons ? String(row.persons).trim() : '',
            category: row.category ? String(row.category).trim() : 'ATENDER',
            desc: String(row.desc).trim(),
            link: row.link ? String(row.link).trim() : ''
          };

          // Enviar a la API con token
          await axios.post(`${API}/posts`, postData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          results.success++;
          
          console.log(`✅ Fila ${i + 2} procesada correctamente`);
        } catch (error) {
          const errorMsg = error.response?.data?.detail || error.message;
          console.error(`❌ Error en fila ${i + 2}:`, errorMsg);
          results.errors.push({
            row: i + 2,
            error: errorMsg,
            data: row
          });
        }
      }

      setResults(results);

      if (results.success > 0) {
        toast.success(`${results.success} registros importados correctamente`);
        await fetchPosts();
      }

      if (results.errors.length > 0) {
        toast.warning(`${results.errors.length} registros con errores`);
      }
    } catch (error) {
      console.error('Error al procesar archivo:', error);
      toast.error('Error al procesar el archivo');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  return (
    <div className="bulk-container fade-in" data-testid="bulk-view">
      <div className="container">
        <div className="panel bulk-panel">
          <div className="bulk-header">
            <div className="bulk-title-section">
              <Upload size={32} className="bulk-icon" />
              <div>
                <h2 className="bulk-title">Carga Masiva de Publicaciones</h2>
                <p className="bulk-subtitle">Importe múltiples registros desde un archivo Excel</p>
              </div>
            </div>
          </div>

          {/* Instrucciones */}
          <div className="instructions-section">
            <h3 className="section-title">
              <Info size={20} />
              Instrucciones de Uso
            </h3>

            <div className="instructions-grid">
              <div className="instruction-card">
                <div className="instruction-number">1</div>
                <div className="instruction-content">
                  <h4>Descargue la Plantilla</h4>
                  <p>Haga clic en el botón "Descargar Plantilla" para obtener el archivo Excel con el formato correcto.</p>
                </div>
              </div>

              <div className="instruction-card">
                <div className="instruction-number">2</div>
                <div className="instruction-content">
                  <h4>Complete los Datos</h4>
                  <p>Abra el archivo y complete cada fila con la información de las publicaciones que desea importar.</p>
                </div>
              </div>

              <div className="instruction-card">
                <div className="instruction-number">3</div>
                <div className="instruction-content">
                  <h4>Verifique el Formato</h4>
                  <p>Asegúrese de que todos los campos obligatorios estén completos y los datos sean correctos.</p>
                </div>
              </div>

              <div className="instruction-card">
                <div className="instruction-number">4</div>
                <div className="instruction-content">
                  <h4>Cargue el Archivo</h4>
                  <p>Seleccione el archivo completado y el sistema procesará automáticamente todos los registros.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Formato de Datos */}
          <div className="format-section">
            <h3 className="section-title">
              <FileSpreadsheet size={20} />
              Formato de Datos Requerido
            </h3>

            <div className="format-table">
              <table>
                <thead>
                  <tr>
                    <th>Columna</th>
                    <th>Descripción</th>
                    <th>Ejemplo</th>
                    <th>Obligatorio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>date</code></td>
                    <td>Fecha del post</td>
                    <td>2025-10-30</td>
                    <td><CheckCircle size={16} className="required-icon" /></td>
                  </tr>
                  <tr>
                    <td><code>author</code></td>
                    <td>Autor de la publicación</td>
                    <td>@usuario_ejemplo</td>
                    <td><CheckCircle size={16} className="required-icon" /></td>
                  </tr>
                  <tr>
                    <td><code>network</code></td>
                    <td>Red social (X, Facebook, Instagram, YouTube, TikTok, Web)</td>
                    <td>X</td>
                    <td><AlertCircle size={16} className="optional-icon" /> Opcional (por defecto: X)</td>
                  </tr>
                  <tr>
                    <td><code>mentioned</code></td>
                    <td>Institución mencionada</td>
                    <td>Ministerio de Educación</td>
                    <td><AlertCircle size={16} className="optional-icon" /> Opcional</td>
                  </tr>
                  <tr>
                    <td><code>persons</code></td>
                    <td>Personas mencionadas</td>
                    <td>Juan Pérez</td>
                    <td className="optional-text">Opcional</td>
                  </tr>
                  <tr>
                    <td><code>category</code></td>
                    <td>Categoría (ATENDER, REVISAR, ARCHIVO)</td>
                    <td>ATENDER</td>
                    <td><AlertCircle size={16} className="optional-icon" /> Opcional (por defecto: ATENDER)</td>
                  </tr>
                  <tr>
                    <td><code>desc</code></td>
                    <td>Descripción de la publicación</td>
                    <td>Descripción detallada...</td>
                    <td><CheckCircle size={16} className="required-icon" /> <strong>Obligatorio</strong></td>
                  </tr>
                  <tr>
                    <td><code>link</code></td>
                    <td>Enlace HTTP/HTTPS</td>
                    <td>https://x.com/post/123</td>
                    <td><AlertCircle size={16} className="optional-icon" /> Opcional</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Acciones */}
          <div className="actions-section">
            <button 
              className="btn btn-secondary btn-lg"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
            >
              <Download size={20} />
              Descargar Plantilla
            </button>

            <div className="upload-wrapper">
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className={`btn btn-primary btn-lg ${uploading ? 'disabled' : ''}`}>
                <Upload size={20} />
                {uploading ? 'Procesando...' : 'Seleccionar Archivo Excel'}
              </label>
            </div>
          </div>

          {/* Resultados */}
          {results && (
            <div className="results-section">
              <h3 className="section-title">Resultados de la Importación</h3>

              <div className="results-summary">
                <div className="result-card result-total">
                  <div className="result-number">{results.total}</div>
                  <div className="result-label">Total procesados</div>
                </div>

                <div className="result-card result-success">
                  <CheckCircle size={24} />
                  <div className="result-number">{results.success}</div>
                  <div className="result-label">Importados correctamente</div>
                </div>

                <div className="result-card result-error">
                  <AlertCircle size={24} />
                  <div className="result-number">{results.errors.length}</div>
                  <div className="result-label">Con errores</div>
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="errors-list">
                  <h4>Detalles de Errores:</h4>
                  <div className="error-items">
                    {results.errors.map((err, idx) => (
                      <div key={idx} className="error-item">
                        <div className="error-header">
                          <AlertCircle size={16} />
                          <span><strong>Fila {err.row}:</strong> {err.error}</span>
                        </div>
                        {err.data && (
                          <div className="error-data">
                            <small>
                              Datos: {JSON.stringify(err.data).substring(0, 100)}
                              {JSON.stringify(err.data).length > 100 ? '...' : ''}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;